import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { Loader2, CalendarDays, CheckCircle2, XCircle, Clock, RefreshCw, UserPlus, X, AlertTriangle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _upCodigo = (entrega) => {
  const up = entrega?.unidad_principal;
  return (typeof up === 'object' && up) ? (up.codigo || '') : (up || '');
};

const SeleccionarCita = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const accion = searchParams.get('accion');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [result, setResult] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [autorizaImagen, setAutorizaImagen] = useState(false);
  const [autorizaDatos, setAutorizaDatos] = useState(false);

  // Inyectar font Roboto
  useEffect(() => {
    const linkId = 'roboto-font-link';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Invitados
  const [invitados, setInvitados] = useState([{ nombre: '', documento: '' }]);
  const [showWarning, setShowWarning] = useState(false);

  // max_invitados dinámico desde el API
  const maxInvitados = data?.max_invitados ?? 1;

  useEffect(() => {
    fetch(`${API_URL}/api/configuracion/general`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.logo_url) setLogoUrl(d.logo_url); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/entregas/programaciones/seleccion/${token}`)
      .then(r => {
        if (!r.ok) {
          return r.json().then(d => {
            if (d.detail) throw new Error(d.detail);
            throw new Error('Error loading data');
          });
        }
        return r.json();
      })
      .then(d => {
        if (d.status === 'expired') setResult('expired');
        else if (d.status === 'already_confirmed') {
          setResult('already');
          setData({
            fecha_confirmada: d.fecha_confirmada,
            confirmante_nombre: d.confirmante_nombre,
            confirmante_email: d.confirmante_email,
            confirmado_at: d.confirmado_at,
            invitados_confirmacion: d.invitados_confirmacion,
          });
        }
        else if (d.status === 'reprogramacion_solicitada') setResult('reprogramacion_enviada');
        else {
          setData(d);
          if ((d.opciones_fecha || []).length === 1) setSelectedIdx(0);
        }
      })
      .catch(() => setResult('error'))
      .finally(() => setLoading(false));
  }, [token]);

  const _getGeoLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ geo_status: 'unavailable' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          geo_lat: pos.coords.latitude,
          geo_lng: pos.coords.longitude,
          geo_accuracy: pos.coords.accuracy,
          geo_status: 'granted',
        }),
        () => resolve({ geo_status: 'denied' }),
        { timeout: 8000, enableHighAccuracy: true }
      );
    });
  };

  const getValidInvitados = () => {
    return invitados.filter(inv => inv.nombre.trim() && inv.documento.trim());
  };

  const handleConfirmClick = () => {
    if (selectedIdx === null) return;
    // Si max_invitados es 0, no hay sección de invitados, confirmar directo
    if (maxInvitados === 0) {
      doConfirm();
      return;
    }
    const valid = getValidInvitados();
    if (valid.length === 0) {
      setShowWarning(true);
    } else {
      doConfirm();
    }
  };

  const doConfirm = async () => {
    setShowWarning(false);
    setConfirming(true);
    try {
      const geo = await _getGeoLocation();
      const validInvitados = getValidInvitados();
      const auditData = {
        opcion_idx: selectedIdx,
        invitados: validInvitados,
        autoriza_imagen: autorizaImagen,
        autoriza_datos: autorizaDatos,
        ...geo,
        client_timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        platform: navigator.platform || '',
        language: navigator.language || '',
      };

      const resp = await fetch(`${API_URL}/api/entregas/programaciones/seleccion/${token}/confirmar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(auditData),
      });
      const body = await resp.json();
      if (resp.ok) {
        setResult('confirmed');
        setData(prev => ({ ...prev, confirmed_fecha: body.fecha_confirmada, confirmed_invitados: validInvitados }));
      } else if (resp.status === 409) {
        // Race condition — otra persona ya confirmó
        const d = body.detail || {};
        setResult('race_condition');
        setData({
          fecha_confirmada: d.fecha_confirmada,
          confirmante_nombre: d.confirmante_nombre,
          confirmante_email: d.confirmante_email,
          confirmado_at: d.confirmado_at,
          invitados_confirmacion: d.invitados_confirmacion,
        });
      } else {
        if (typeof body.detail === 'string' && body.detail.includes('expirado')) setResult('expired');
        else alert(typeof body.detail === 'string' ? body.detail : 'Error al confirmar');
      }
    } catch {
      alert('Error de conexión');
    } finally { setConfirming(false); }
  };

  const handleSolicitarReprogramacion = async () => {
    setRequesting(true);
    try {
      const resp = await fetch(`${API_URL}/api/entregas/programaciones/seleccion/${token}/solicitar-reprogramacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await resp.json();
      if (resp.ok) {
        setResult('reprogramacion_enviada');
      } else {
        if (body.detail?.includes('expirado')) setResult('expired');
        else if (body.detail?.includes('confirmada')) setResult('already');
        else alert(body.detail || 'Error al solicitar reprogramación');
      }
    } catch {
      alert('Error de conexión');
    } finally { setRequesting(false); }
  };

  // Invitados handlers
  const updateInvitado = (idx, field, value) => {
    setInvitados(prev => prev.map((inv, i) => i === idx ? { ...inv, [field]: value } : inv));
  };
  const addInvitado = () => {
    if (invitados.length < maxInvitados) {
      setInvitados(prev => [...prev, { nombre: '', documento: '' }]);
    }
  };
  const removeInvitado = (idx) => {
    if (invitados.length > 1) {
      setInvitados(prev => prev.filter((_, i) => i !== idx));
    } else {
      setInvitados([{ nombre: '', documento: '' }]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#5B7FA6]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center p-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          {logoUrl ? (
            <img src={logoUrl} alt="IMAGINA" className="h-14 w-auto mx-auto mb-1 object-contain" />
          ) : (
            <h1 className="text-2xl font-bold text-[#5B7FA6] italic">IMAGINA</h1>
          )}
          <p className="text-sm text-[#888]">Sistema de Entregas Inmobiliarias</p>
        </div>

        {result === 'expired' && (
          <Card className="border border-orange-200" data-testid="cita-expired">
            <CardContent className="py-10 text-center">
              <XCircle size={40} className="mx-auto text-orange-400 mb-3" />
              <h2 className="text-lg font-semibold text-orange-700 mb-2">Enlace Expirado</h2>
              <p className="text-sm text-[#888]">Este enlace ya no es válido. Por favor, contacte a su ejecutivo para reprogramar.</p>
            </CardContent>
          </Card>
        )}

        {result === 'already' && (
          <Card className="border border-green-200" data-testid="cita-already">
            <CardContent className="py-10 text-center">
              <CheckCircle2 size={40} className="mx-auto text-green-500 mb-3" />
              <h2 className="text-lg font-semibold text-green-700 mb-2">Cita ya Confirmada</h2>
              <p className="text-sm text-[#888]">Usted ya seleccionó una fecha para su entrega. Nos veremos pronto.</p>

              {data?.fecha_confirmada && (() => {
                const fc = data.fecha_confirmada;
                const datePart = fc.substring(0, 10); // "2026-10-05"
                const timePart = fc.substring(11, 16); // "09:00"
                const [y, m, d] = datePart.split('-');
                const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','setiembre','octubre','noviembre','diciembre'];
                const fechaTexto = `${parseInt(d)} de ${meses[parseInt(m) - 1]} de ${y}`;
                return (
                  <div className="inline-block bg-green-50 border border-green-200 rounded-lg px-6 py-3 mt-4" data-testid="already-fecha-info">
                    <p className="text-xs text-[#888] mb-1">Fecha de entrega confirmada:</p>
                    <p className="text-base font-bold text-[#2D2D2D]">{fechaTexto}</p>
                    <p className="text-sm text-[#5A5A5A]">Hora de inicio: {timePart}</p>
                  </div>
                );
              })()}

              {data?.confirmante_nombre && (
                <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-lg px-5 py-3 mt-4 text-left mx-auto max-w-xs" data-testid="already-confirmante-info">
                  <p className="text-[11px] font-semibold text-[#888] uppercase mb-2">Confirmado por</p>
                  <p className="text-sm text-[#333]">{data.confirmante_nombre}</p>
                  {data.confirmante_email && <p className="text-xs text-[#888]">{data.confirmante_email}</p>}
                  {data.confirmado_at && (
                    <p className="text-xs text-[#888] mt-1">
                      {new Date(data.confirmado_at).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}

              {data?.invitados_confirmacion && data.invitados_confirmacion.length > 0 && (
                <div className="text-left bg-[#fafafa] border border-[#e8e8e8] rounded-lg p-3 mt-3 mx-auto max-w-xs" data-testid="already-invitados">
                  <p className="text-[11px] font-semibold text-[#888] uppercase mb-2">Invitados registrados</p>
                  {data.invitados_confirmacion.map((inv, i) => (
                    <div key={i} className="flex justify-between text-xs text-[#333] py-1 border-b border-[#f0f0f0] last:border-b-0">
                      <span>{inv.nombre}</span>
                      <span className="text-[#888]">{inv.documento}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 text-xs text-[#888] leading-relaxed" data-testid="already-contacto">
                <p>Cualquier duda o consulta te puedes comunicar con nosotros a{' '}
                  <a href="mailto:sac@imagina.pe" className="text-[#5B7FA6] underline">sac@imagina.pe</a>
                </p>
                <p className="mt-2">Horario de Atenci&oacute;n: Lunes a viernes de 8:30 a 12:30 y de 14:00 a 18:00 hrs.</p>
                <p>Imagina, Av. Rivera Navarrete 395, San Isidro, Lima, Lima, Per&uacute;. (01)611 3855</p>
              </div>
            </CardContent>
          </Card>
        )}

        {result === 'race_condition' && (
          <Card className="border border-amber-200" data-testid="cita-race-condition">
            <CardContent className="py-10 text-center">
              <AlertTriangle size={40} className="mx-auto text-amber-500 mb-3" />
              <h2 className="text-lg font-semibold text-amber-700 mb-2">Cita Confirmada por Otro Usuario</h2>
              <p className="text-sm text-[#888]">Otra persona ya seleccionó la fecha de entrega antes que usted.</p>

              {data?.fecha_confirmada && (() => {
                const fc = data.fecha_confirmada;
                const datePart = fc.substring(0, 10);
                const timePart = fc.substring(11, 16);
                const [y, m, d] = datePart.split('-');
                const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','setiembre','octubre','noviembre','diciembre'];
                const fechaTexto = `${parseInt(d)} de ${meses[parseInt(m) - 1]} de ${y}`;
                return (
                  <div className="inline-block bg-amber-50 border border-amber-200 rounded-lg px-6 py-3 mt-4" data-testid="race-fecha-info">
                    <p className="text-xs text-[#888] mb-1">Fecha de entrega confirmada:</p>
                    <p className="text-base font-bold text-[#2D2D2D]">{fechaTexto}</p>
                    <p className="text-sm text-[#5A5A5A]">Hora de inicio: {timePart}</p>
                  </div>
                );
              })()}

              {data?.confirmante_nombre && (
                <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-lg px-5 py-3 mt-4 text-left mx-auto max-w-xs" data-testid="race-confirmante-info">
                  <p className="text-[11px] font-semibold text-[#888] uppercase mb-2">Confirmado por</p>
                  <p className="text-sm text-[#333]">{data.confirmante_nombre}</p>
                  {data.confirmante_email && <p className="text-xs text-[#888]">{data.confirmante_email}</p>}
                  {data.confirmado_at && (
                    <p className="text-xs text-[#888] mt-1">
                      {new Date(data.confirmado_at).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}

              {data?.invitados_confirmacion && data.invitados_confirmacion.length > 0 && (
                <div className="text-left bg-[#fafafa] border border-[#e8e8e8] rounded-lg p-3 mt-3 mx-auto max-w-xs" data-testid="race-invitados">
                  <p className="text-[11px] font-semibold text-[#888] uppercase mb-2">Invitados registrados</p>
                  {data.invitados_confirmacion.map((inv, i) => (
                    <div key={i} className="flex justify-between text-xs text-[#333] py-1 border-b border-[#f0f0f0] last:border-b-0">
                      <span>{inv.nombre}</span>
                      <span className="text-[#888]">{inv.documento}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 text-xs text-[#888] leading-relaxed" data-testid="race-contacto">
                <p>Cualquier duda o consulta te puedes comunicar con nosotros a{' '}
                  <a href="mailto:sac@imagina.pe" className="text-[#5B7FA6] underline">sac@imagina.pe</a>
                </p>
                <p className="mt-2">Horario de Atenci&oacute;n: Lunes a viernes de 8:30 a 12:30 y de 14:00 a 18:00 hrs.</p>
                <p>Imagina, Av. Rivera Navarrete 395, San Isidro, Lima, Lima, Per&uacute;. (01)611 3855</p>
              </div>
            </CardContent>
          </Card>
        )}

        {result === 'confirmed' && (
          <Card className="border border-green-200" data-testid="cita-confirmed">
            <CardContent className="py-10 text-center">
              <CheckCircle2 size={48} className="mx-auto text-green-500 mb-3" />
              <h2 className="text-xl font-bold text-green-700 mb-2">Fecha Confirmada</h2>
              <p className="text-sm text-[#888] mb-4">Su cita de entrega ha sido registrada exitosamente.</p>
              {data?.opciones_fecha?.[selectedIdx] && (() => {
                const rawFecha = data.opciones_fecha[selectedIdx].fecha;
                const parts = rawFecha.split('-');
                const fechaFormatted = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : rawFecha;
                return (
                  <div className="inline-block bg-green-50 border border-green-200 rounded-lg px-6 py-3 mb-4">
                    <p className="text-xl font-bold text-[#2D2D2D]">{fechaFormatted}</p>
                    <p className="text-base text-[#5A5A5A]">Hora de inicio: {data.opciones_fecha[selectedIdx].hora_inicio}</p>
                  </div>
                );
              })()}
              {data?.confirmed_invitados && data.confirmed_invitados.length > 0 && (
                <div className="text-left bg-[#fafafa] border border-[#e8e8e8] rounded-lg p-3 mt-2 mx-auto max-w-xs" data-testid="invitados-confirmados">
                  <p className="text-[11px] font-semibold text-[#888] uppercase mb-2">Invitados registrados</p>
                  {data.confirmed_invitados.map((inv, i) => (
                    <div key={i} className="flex justify-between text-xs text-[#333] py-1 border-b border-[#f0f0f0] last:border-b-0">
                      <span>{inv.nombre}</span>
                      <span className="text-[#888]">{inv.documento}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-center gap-1 text-[11px] text-[#5B7FA6] mt-4" data-testid="email-confirmacion-note">
                <Mail size={14} />
                <span>Se ha enviado un correo de confirmación a su email {data?.entrega?.cliente_email}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {result === 'error' && (
          <Card className="border border-red-200">
            <CardContent className="py-10 text-center">
              <XCircle size={40} className="mx-auto text-red-400 mb-3" />
              <h2 className="text-lg font-semibold text-red-700">Error</h2>
              <p className="text-sm text-[#888]">No se pudo cargar la información. Verifique el enlace e intente nuevamente.</p>
            </CardContent>
          </Card>
        )}

        {result === 'reprogramacion_enviada' && (
          <Card className="border border-orange-200" data-testid="cita-reprogramacion-enviada">
            <CardContent className="py-10 text-center">
              <RefreshCw size={40} className="mx-auto text-orange-500 mb-3" />
              <h2 className="text-lg font-semibold text-orange-700 mb-2">Solicitud Enviada</h2>
              <p className="text-sm text-[#888]">Su solicitud de reprogramación ha sido registrada. El arquitecto le contactará con nuevas opciones de fecha.</p>
            </CardContent>
          </Card>
        )}

        {!result && data && accion === 'reprogramar' && (
          <Card className="border border-[#E8E8E7]" data-testid="cita-solicitar-reprogramacion">
            <CardContent className="pt-6 pb-4 space-y-5">
              <div className="text-center">
                <RefreshCw size={28} className="mx-auto text-orange-500 mb-2" />
                <h2 className="text-lg font-bold text-[#2D2D2D]">Solicitar Otra Fecha</h2>
                {data.entrega && (
                  <p className="text-sm text-[#888] mt-1">
                    {data.entrega.proyecto_nombre} — {_upCodigo(data.entrega)}
                  </p>
                )}
              </div>
              <p className="text-sm text-[#5A5A5A] text-center">
                Si ninguna de las fechas propuestas le conviene, puede solicitar al Arquitecto de Entregas que le ofrezca nuevas opciones.
              </p>
              <Button
                onClick={handleSolicitarReprogramacion}
                disabled={requesting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3"
                data-testid="btn-solicitar-reprogramacion"
              >
                {requesting ? <Loader2 size={16} className="animate-spin mr-2" /> : <RefreshCw size={16} className="mr-2" />}
                Solicitar Nueva Fecha
              </Button>
            </CardContent>
          </Card>
        )}

        {!result && data && accion !== 'reprogramar' && (
          <Card className="border border-[#E8E8E7]" data-testid="cita-selector">
            <CardContent className="pt-6 pb-4 space-y-5">
              <div className="text-center">
                <CalendarDays size={28} className="mx-auto text-[#5B7FA6] mb-2" />
                <h2 className="text-lg font-bold text-[#2D2D2D]">Seleccione su Fecha de Entrega</h2>
                {data.entrega && (
                  <p className="text-sm text-[#888] mt-1">
                    {data.entrega.proyecto_nombre} — {_upCodigo(data.entrega)}
                  </p>
                )}
                {data.entregas_vinculadas && data.entregas_vinculadas.length > 0 && (
                  <div className="mt-2 text-xs text-[#888] bg-[#f8f8f8] rounded px-3 py-2 inline-block" data-testid="vinculadas-info">
                    <p className="font-semibold text-[#666] mb-1">Unidades incluidas:</p>
                    {data.entregas_vinculadas.map((v, i) => (
                      <p key={i}>{v.unidad_nombre} ({v.tipo_unidad}) — {v.codigo}</p>
                    ))}
                  </div>
                )}
              </div>

              {data.token_expira && (
                <div className="flex items-center justify-center gap-1 text-xs text-orange-600">
                  <Clock size={12} />
                  <span>Este enlace expira el {new Date(data.token_expira).toLocaleString('es-PE')}</span>
                </div>
              )}

              <div className="space-y-3">
                {(data.opciones_fecha || []).map((op, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedIdx(i)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      selectedIdx === i
                        ? 'border-[#5B7FA6] bg-[#5B7FA6]/5 shadow-sm'
                        : 'border-[#E8E8E7] hover:border-[#5B7FA6]/40'
                    }`}
                    data-testid={`opcion-publica-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#888]">Opción {i + 1}</p>
                        <p className="text-lg font-bold text-[#2D2D2D]">{(() => { const p = op.fecha.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : op.fecha; })()}</p>
                        <p className="text-sm text-[#5A5A5A]">Hora de inicio: {op.hora_inicio}</p>
                      </div>
                      {selectedIdx === i && <CheckCircle2 size={24} className="text-[#5B7FA6]" />}
                    </div>
                  </button>
                ))}
              </div>

              {/* Invitados Section — only if max_invitados > 0 */}
              {maxInvitados > 0 && (
              <div className="border-t border-dashed border-[#d4c5bb] pt-4" data-testid="invitados-section">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] font-semibold text-[#2D2D2D] flex items-center gap-1.5">
                    <UserPlus size={14} className="text-[#5B7FA6]" />
                    Invitado(s)
                  </span>
                  <span className="text-[10px] text-[#888]">{maxInvitados === 1 ? 'Máximo 1 invitado' : `${invitados.length} de ${maxInvitados} máximo`}</span>
                </div>

                <div className="space-y-2">
                  {invitados.map((inv, idx) => (
                    <div key={idx} className="flex gap-2 items-start" data-testid={`invitado-row-${idx}`}>
                      <div className="flex-1">
                        <label className="block text-[10px] font-semibold text-[#888] uppercase mb-1">Nombre</label>
                        <input
                          type="text"
                          value={inv.nombre}
                          onChange={(e) => updateInvitado(idx, 'nombre', e.target.value)}
                          placeholder="Nombre completo"
                          className="w-full px-2.5 py-2 border border-[#e0e0e0] rounded-md text-sm bg-[#fafafa] focus:bg-white focus:border-[#5B7FA6] outline-none transition-colors"
                          data-testid={`invitado-nombre-${idx}`}
                        />
                      </div>
                      <div className="w-[120px]">
                        <label className="block text-[10px] font-semibold text-[#888] uppercase mb-1">Documento</label>
                        <input
                          type="text"
                          value={inv.documento}
                          onChange={(e) => updateInvitado(idx, 'documento', e.target.value)}
                          placeholder="DNI / CE"
                          className="w-full px-2.5 py-2 border border-[#e0e0e0] rounded-md text-sm bg-[#fafafa] focus:bg-white focus:border-[#5B7FA6] outline-none transition-colors"
                          data-testid={`invitado-documento-${idx}`}
                        />
                      </div>
                      <button
                        onClick={() => removeInvitado(idx)}
                        className="mt-[18px] w-8 h-8 flex items-center justify-center border border-[#e8e8e8] rounded-md text-[#999] hover:bg-red-50 hover:border-red-300 hover:text-red-500 transition-colors shrink-0"
                        data-testid={`invitado-remove-${idx}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                {invitados.length < maxInvitados && (
                  <button
                    onClick={addInvitado}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#5B7FA6] border border-dashed border-[#5B7FA6] rounded-md px-3 py-1.5 hover:bg-[#5B7FA6]/5 transition-colors"
                    data-testid="btn-add-invitado"
                  >
                    <UserPlus size={12} />
                    Agregar invitado
                  </button>
                )}
              </div>
              )}

              {/* Autorizaciones */}
              <div style={{ backgroundColor: '#EAE5DD', borderRadius: '8px', padding: '16px 20px', marginTop: '16px' }} data-testid="seccion-autorizaciones">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginBottom: '14px' }}>
                  <input
                    type="checkbox"
                    checked={autorizaImagen}
                    onChange={(e) => setAutorizaImagen(e.target.checked)}
                    style={{ marginTop: '3px', accentColor: '#5B7FA6', width: '16px', height: '16px', flexShrink: 0 }}
                    data-testid="chk-autoriza-imagen"
                  />
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#444', lineHeight: '1.5' }}>
                    Autorizo el uso de mi imagen en fotografías y/o videos que puedan ser tomados durante la entrega de mi departamento, con fines institucionales de comunicación de Imagina Inmobiliaria.
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autorizaDatos}
                    onChange={(e) => setAutorizaDatos(e.target.checked)}
                    style={{ marginTop: '3px', accentColor: '#5B7FA6', width: '16px', height: '16px', flexShrink: 0 }}
                    data-testid="chk-autoriza-datos"
                  />
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#444', lineHeight: '1.5' }}>
                    Autorizo el tratamiento de mis datos personales conforme a la{' '}
                    <a href="https://www.imagina.pe/politica-de-privacidad-de-imagina/politica-de-privacidad" target="_blank" rel="noopener noreferrer" style={{ color: '#5B7FA6', textDecoration: 'underline', fontWeight: 500 }}>
                      Política de Privacidad
                    </a>{' '}
                    de Imagina Inmobiliaria, para fines relacionados con la entrega de mi inmueble y servicios posteriores.
                    <span style={{ color: '#DC2626' }}> *</span>
                  </span>
                </label>
              </div>

              <Button
                onClick={handleConfirmClick}
                disabled={selectedIdx === null || confirming || !autorizaDatos}
                className="w-full bg-[#5B7FA6] hover:bg-[#4a6d92] text-white py-3"
                data-testid="btn-confirmar-cita-publica"
              >
                {confirming ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
                Confirmar esta Fecha
              </Button>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-[#aaa] mt-4">IMAGINA Inmobiliaria — Sistema de Postventa</p>

        {/* Warning Dialog: Sin invitados */}
        <Dialog open={showWarning} onOpenChange={setShowWarning}>
          <DialogContent className="sm:max-w-[400px]" data-testid="dialog-warning-invitados">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-sm">
                <AlertTriangle size={18} className="text-amber-500" />
                Sin invitados registrados
              </DialogTitle>
              <DialogDescription className="text-sm text-[#666] pt-2">
                ¿Está seguro de confirmar su cita sin registrar invitados? Puede agregar hasta {maxInvitados} {maxInvitados === 1 ? 'persona' : 'personas'} que lo acompañarán.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setShowWarning(false)}
                data-testid="btn-cancel-warning"
              >
                Cancelar
              </Button>
              <Button
                onClick={doConfirm}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                disabled={confirming}
                data-testid="btn-confirmar-sin-invitados"
              >
                {confirming ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Confirmar sin invitados
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SeleccionarCita;
